import React, { useState, useEffect, useCallback } from 'react';
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  MoreHorizontal,
  Edit2,
  Trash2,
  Users,
  FileText,
  Layers,
  AlertTriangle,
} from 'lucide-react';

/**
 * Workspace Admin Manager
 * Admin UI for managing workspaces within an Account.
 * P0 TENANT ISOLATION: Accepts tenantId prop for SuperAdmin cross-tenant access.
 */
const WorkspaceAdminManager = ({ className = "", tenantId = null }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dialog states
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editWorkspace, setEditWorkspace] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, workspace: null });
  
  // Form state
  const [formData, setFormData] = useState({ name: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchWorkspaces = useCallback(async () => {
    try {
      setLoading(true);
      const params = tenantId ? { include_inactive: true, tenant_id: tenantId } : { include_inactive: true };
      const response = await api.get('/tenants/workspaces/all', { params });
      setWorkspaces(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    
    try {
      setSubmitting(true);
      const slug = formData.name.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_');
      await api.post('/tenants/workspaces', {
        name: formData.name.trim(),
        slug: slug,
      });
      
      toast({ title: "Workspace created" });
      setFormData({ name: "" });
      setCreateOpen(false);
      fetchWorkspaces();
      
    } catch (err) {
      toast({
        title: "Failed to create workspace",
        description: err.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!editWorkspace) return;
    if (!formData.name.trim()) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    
    try {
      setSubmitting(true);
      await api.put(`/tenants/workspaces/${editWorkspace.id}`, {
        name: formData.name.trim(),
      });
      
      toast({ title: "Workspace updated" });
      setEditOpen(false);
      setEditWorkspace(null);
      fetchWorkspaces();
      
    } catch (err) {
      toast({
        title: "Failed to update workspace",
        description: err.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (workspace) => {
    const newStatus = workspace.status === "active" ? "inactive" : "active";
    
    try {
      await api.put(`/tenants/workspaces/${workspace.id}`, {
        status: newStatus,
      });
      fetchWorkspaces();
      toast({
        title: `Workspace ${newStatus === "active" ? "activated" : "deactivated"}`,
      });
    } catch (err) {
      toast({
        title: "Failed to update status",
        description: err.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.workspace) return;
    
    try {
      await api.delete(`/tenants/workspaces/${deleteConfirm.workspace.id}`);
      toast({ title: "Workspace deleted" });
      setDeleteConfirm({ open: false, workspace: null });
      fetchWorkspaces();
    } catch (err) {
      toast({
        title: "Cannot delete workspace",
        description: err.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    }
  };

  const openEditDialog = (workspace) => {
    setEditWorkspace(workspace);
    setFormData({ name: workspace.name });
    setEditOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Card className={`glass border-white/10 ${className}`}>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-80" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={className} data-testid="workspace-admin-manager">
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl text-white flex items-center gap-2">
                <Layers className="h-5 w-5 text-indigo-400" />
                Workspaces
              </CardTitle>
              <CardDescription className="text-slate-400 mt-1">
                Manage data partitions within your Account
              </CardDescription>
            </div>
            <Button
              onClick={() => {
                setFormData({ name: "" });
                setCreateOpen(true);
              }}
              size="sm"
              data-testid="create-workspace-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Workspace
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-sm text-red-400 p-4 bg-red-500/10 rounded-lg">
              {error}
            </div>
          ) : workspaces.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Layers className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No workspaces found</p>
              <p className="text-xs mt-1">Create a workspace to partition your data</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-slate-400">Name</TableHead>
                  <TableHead className="text-slate-400">Users</TableHead>
                  <TableHead className="text-slate-400">Leads</TableHead>
                  <TableHead className="text-slate-400">Created</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workspaces.map((workspace) => (
                  <TableRow 
                    key={workspace.id} 
                    className="border-white/10" 
                    data-testid={`workspace-row-${workspace.id}`}
                  >
                    <TableCell className="font-medium text-slate-200">
                      <div className="flex items-center gap-2">
                        <Layers className="h-4 w-4 text-slate-400" />
                        {workspace.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-300">
                        <Users className="h-3 w-3" />
                        {workspace.user_count || 0}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-300">
                        <FileText className="h-3 w-3" />
                        {workspace.lead_count || 0}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-400 text-sm">
                      {formatDate(workspace.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={workspace.status === "active" || !workspace.status}
                          onCheckedChange={() => handleToggleStatus(workspace)}
                          aria-label={`Toggle ${workspace.name} status`}
                          data-testid={`toggle-workspace-${workspace.id}`}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon-sm"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-slate-950 border-white/10 ring-1 ring-white/10">
                          <DropdownMenuItem
                            onClick={() => openEditDialog(workspace)}
                            className="text-slate-200 focus:bg-slate-800 focus:text-slate-100"
                          >
                            <Edit2 className="h-4 w-4 mr-2" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-white/10" />
                          <DropdownMenuItem
                            onClick={() => setDeleteConfirm({ open: true, workspace })}
                            className="text-rose-300 focus:bg-rose-500/20 focus:text-rose-200"
                            disabled={(workspace.user_count || 0) > 0 || (workspace.lead_count || 0) > 0}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Create Workspace</DialogTitle>
            <DialogDescription className="text-slate-400">
              Workspaces partition data within your Account
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-200">
                Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="name"
                placeholder="e.g., Retirement Planning"
                value={formData.name}
                onChange={(e) => setFormData({ name: e.target.value })}
                className="bg-white/5 border-white/10 text-slate-100"
                data-testid="workspace-name-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="secondary" 
              onClick={() => setCreateOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={submitting || !formData.name.trim()}
              data-testid="create-workspace-submit"
            >
              {submitting ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Rename Workspace</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name" className="text-slate-200">
                Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ name: e.target.value })}
                className="bg-white/5 border-white/10 text-slate-100"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="secondary" 
              onClick={() => setEditOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEdit}
              disabled={submitting || !formData.name.trim()}
              data-testid="edit-workspace-submit"
            >
              {submitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => setDeleteConfirm({ ...deleteConfirm, open })}>
        <AlertDialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Workspace</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {deleteConfirm.workspace && (
                (deleteConfirm.workspace.user_count > 0 || deleteConfirm.workspace.lead_count > 0) ? (
                  <div className="flex items-start gap-2 text-amber-400">
                    <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    <span>
                      Cannot delete "{deleteConfirm.workspace.name}" because it has 
                      {deleteConfirm.workspace.user_count > 0 && ` ${deleteConfirm.workspace.user_count} user(s)`}
                      {deleteConfirm.workspace.user_count > 0 && deleteConfirm.workspace.lead_count > 0 && ' and'}
                      {deleteConfirm.workspace.lead_count > 0 && ` ${deleteConfirm.workspace.lead_count} lead(s)`}
                      {' '}assigned. Deactivate instead or reassign first.
                    </span>
                  </div>
                ) : (
                  `Are you sure you want to delete "${deleteConfirm.workspace.name}"? This action cannot be undone.`
                )
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-600 text-slate-200 hover:bg-slate-800">
              Cancel
            </AlertDialogCancel>
            {deleteConfirm.workspace && 
             (deleteConfirm.workspace.user_count || 0) === 0 && 
             (deleteConfirm.workspace.lead_count || 0) === 0 && (
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-rose-500/90 text-white hover:bg-rose-500"
              >
                Delete
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WorkspaceAdminManager;
